/**
 * Stock criticality bands, one copy for the whole app.
 *
 * Matches the backend (housingram-backend utils/inventoryActivation):
 *   red / Critical   < 100
 *   orange / Warning [100, 125)
 *   yellow / OK      [125, 150)
 *   green / Excess   >= 150
 *
 * NULL means Min Qty is 0: the item has no target, so it is "Not set". It is
 * never Critical and never counted in the red share. `Number(null) || 0` used
 * to turn it into 0, which is why every unconfigured item showed red.
 */

export const CRITICALITY_BANDS = {
  not_set: { key: 'not_set', label: 'Not set', color: '#9ca3af', cell: 'bg-gray-100 text-gray-500' },
  red: { key: 'red', label: 'Critical', color: '#f25555', cell: 'bg-red-400' },
  orange: { key: 'orange', label: 'Warning', color: '#eda24b', cell: 'bg-orange-200' },
  yellow: { key: 'yellow', label: 'OK', color: '#facc15', cell: 'bg-yellow-100' },
  green: { key: 'green', label: 'Excess', color: '#22c55e', cell: 'bg-green-100' }
};

/** True when the value is missing ("Not set"), including blank strings. */
export const isCriticalityNotSet = (value) =>
  value === null || value === undefined || value === '' || !Number.isFinite(Number(value));

/** Band key for a stock_criticality value. */
export const criticalityBandKey = (value) => {
  if (isCriticalityNotSet(value)) return 'not_set';
  const c = Number(value);
  if (c < 100) return 'red';
  if (c < 125) return 'orange';
  if (c < 150) return 'yellow';
  return 'green';
};

/** Band object ({ key, label, color, cell }) for a stock_criticality value. */
export const criticalityBand = (value) => CRITICALITY_BANDS[criticalityBandKey(value)];

/** "Not set" is never Critical. */
export const isCriticalityCritical = (value) => criticalityBandKey(value) === 'red';

/** Display text for a criticality cell: the number, or "Not set". */
export const formatCriticality = (value) =>
  isCriticalityNotSet(value) ? 'Not set' : String(Math.round(Number(value)));
